import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface VersionNavigationParams {
  projectId: string;
  cohortId: string;
  cohortVersionId: string;
}

/**
 * Encapsulates silent URL navigation when the backend creates or reverts to a version.
 * Uses `history.replaceState` to update the URL without triggering React Router remount.
 *
 * Returns a stable `handleVersionNavigation` callback that reads from refs,
 * so it is safe to call from socket handlers without stale closures.
 */
export const useVersionNavigation = (params: VersionNavigationParams) => {
  const { projectId, cohortId, cohortVersionId } = params;
  const navigate = useNavigate();
  const location = useLocation();

  const cohortVersionIdRef = useRef(cohortVersionId);
  const paramsRef = useRef({ projectId, cohortId });
  const locationRef = useRef(location);
  const navigateRef = useRef(navigate);

  useEffect(() => {
    cohortVersionIdRef.current = cohortVersionId;
    paramsRef.current = { projectId, cohortId };
    locationRef.current = location;
    navigateRef.current = navigate;
  }, [cohortVersionId, projectId, cohortId, location, navigate]);

  /**
   * Navigate to a new cohort version, updating the URL.
   * @param newVersionId - The version to navigate to
   * @param silent - If true, uses `history.replaceState` (no React Router lifecycle).
   *                 If false, uses React Router `navigate` with `replace: true`.
   */
  // Referentially stable (empty deps — everything is read from refs) so it can
  // safely sit in a dependency array without retriggering the effect that
  // depends on it every render (that retrigger, if the effect itself writes
  // to shared state, is a max-update-depth loop waiting to happen).
  const handleVersionNavigation = useCallback((newVersionId: string, silent = true) => {
    const { projectId: pid, cohortId: cid } = paramsRef.current;
    const currentVersionId = cohortVersionIdRef.current;
    const currentLocation = locationRef.current;
    const currentPathname = currentLocation.pathname;
    const currentSearch = currentLocation.search;

    if (!newVersionId || newVersionId === currentVersionId) return;

    let newPath: string;
    if (currentPathname.includes('/cohort-version/')) {
      newPath = currentPathname.replace(
        /\/cohort-version\/[^/]+/,
        `/cohort-version/${newVersionId}`
      );
    } else {
      newPath = `/projects/${pid}/cohorts/${cid}/cohort-version/${newVersionId}/cohort-builder`;
    }

    const fullPath = newPath + currentSearch;

    if (silent) {
      window.history.replaceState(null, '', fullPath);
      cohortVersionIdRef.current = newVersionId;
    } else {
      navigateRef.current(fullPath, { replace: true });
    }
  }, []);

  return { handleVersionNavigation, cohortVersionIdRef };
};
